import React from 'react';
import PropTypes from 'prop-types';
import camelcaseKeys from 'camelcase-keys';

import Errors from 'components/form/errors';
import HorizontalForm from 'components/form/horizontal_form';
import { createCompany } from './api';
import { COUNTRY_OPTIONS, getStateOptions, stateFieldLabel } from './country_state_list';

export default class Form extends React.Component {
  static propTypes = {
    additionalFields: PropTypes.node,
    availableDatabaseOptions: PropTypes.arrayOf(PropTypes.array),
    lockedDatabaseId: PropTypes.string,
    projectId: PropTypes.number,
    onCancel: PropTypes.func,
    onSave: PropTypes.func
  };

  static defaultProps = {
    availableDatabaseOptions: []
  }

  state = {
    address: {
      primary: true,
      label: '',
      countryId: '1',
      countryNameOverride: '',
      addressOne: '',
      addressTwo: '',
      city: '',
      state: 'AL',
      zip: ''
    },
    contact: {
      name: '',
      email: '',
      phoneNumber: '',
      databaseId: this.props.lockedDatabaseId,
      projectId: ''
    },
    isSaving: false,
    errors: [],
    showAddressFields: false
  }

  anyAvailableDatabases = () => this.props.availableDatabaseOptions.length > 0;

  contact = () => {
    const { contact } = this.state;
    const { projectId } = this.props;
    return this.anyAvailableDatabases() ? contact : { ...contact, projectId };
  }

  saveCompany = () => {
    this.setState({ isSaving: true, errors: [] });
    const { address, showAddressFields } = this.state;
    createCompany(this.contact(), showAddressFields ? address : null).then((contact) => {
      this.props.onSave({
        associatedModelId: contact.id,
        label: contact.name,
        associatedModelType: contact.contactType
      });
    })
      .catch(xhr => this.setState({ errors: camelcaseKeys(xhr.response.data).errors }))
      .then(() => this.setState({ isSaving: !this.anyAvailableDatabases() }));
  }

  handleChange = (e) => {
    const { name } = e.target;
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    this.setState((prevState) => {
      return { contact: { ...prevState.contact, [name]: value } };
    });
  }

  handleAddressChange = (e) => {
    const { name } = e.target;
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    this.setState((prevState) => {
      if (name === 'countryId') {
        const states = getStateOptions(value);
        return { address: { ...prevState.address, [name]: value, ['state']: states.length > 0 ? states[0][1] : '' } };
      }
      return { address: { ...prevState.address, [name]: value } };
    });
  }

  handleCountryChange = (e) => {
    const { name } = e.target;
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    this.setState((prevState) => {
      return { address: { ...prevState.address, [name]: value } };
    });
  }

  toggleAddressFields = () => {
    this.setState((prevState) => {
      return { showAddressFields: !prevState.showAddressFields };
    });
  }

  isValid = () => {
    if (this.state.isSaving) return false;
    const dbSelectionValid = !this.anyAvailableDatabases() || this.state.contact.databaseId;
    return !!(dbSelectionValid && this.state.contact.name);
  }

  renderAddressFields() {
    const stateOptions = getStateOptions(this.state.address.countryId);

    return (
      this.state.showAddressFields &&
      <React.Fragment>
        <HorizontalForm.TextInput
          label="Address Name"
          name="label"
          value={this.state.address.label}
          onChange={this.handleAddressChange}
        />

        <HorizontalForm.Select
          label="Country"
          name="countryId"
          options={COUNTRY_OPTIONS}
          value={this.state.address.countryId}
          onChange={this.handleAddressChange}
        />

        { this.state.address.countryId == 0 &&
          <HorizontalForm.TextInput
            name="countryNameOverride"
            required
            value={this.state.address.countryNameOverride}
            onChange={this.handleAddressChange}
          />
        }

        <HorizontalForm.TextInput
          label="Address Line 1"
          name="addressOne"
          value={this.state.address.addressOne}
          onChange={this.handleAddressChange}
        />

        <HorizontalForm.TextInput
          label="Address Line 2"
          name="addressTwo"
          value={this.state.address.addressTwo}
          onChange={this.handleAddressChange}
        />

        <HorizontalForm.TextInput
          name="city"
          value={this.state.address.city}
          onChange={this.handleAddressChange}
        />

        { stateOptions.length > 0 ?
          <HorizontalForm.Select
            label={stateFieldLabel(this.state.address.countryId)}
            name="state"
            options={stateOptions}
            value={this.state.address.state}
            onChange={this.handleAddressChange}
          /> :
          <HorizontalForm.TextInput
            label={stateFieldLabel(this.state.address.countryId)}
            name="state"
            value={this.state.address.state}
            onChange={this.handleAddressChange}
          />
        }

        <HorizontalForm.TextInput
          name="zip"
          value={this.state.address.zip}
          onChange={this.handleAddressChange}
        />
      </React.Fragment>
    );
  }

  render() {
    return (
      <div>
        <Errors errors={this.state.errors}/>

        <HorizontalForm>
          {this.anyAvailableDatabases() &&
            <React.Fragment>
              <p>This company will be saved to the database specified below.</p>

              <HorizontalForm.Select
                disabled={!!this.props.lockedDatabaseId}
                includeBlank="-- select a database --"
                label="Database"
                name="databaseId"
                options={this.props.availableDatabaseOptions}
                required
                value={this.state.contact.databaseId}
                onChange={this.handleChange}
              />
            </React.Fragment>
          }

          <HorizontalForm.TextInput
            label="Company Name"
            name="name"
            required
            value={this.state.contact.name}
            onChange={this.handleChange}
          />

          <HorizontalForm.TextInput
            name="email"
            value={this.state.contact.email}
            onChange={this.handleChange}
          />

          <HorizontalForm.TextInput
            name="phoneNumber"
            value={this.state.contact.phoneNumber}
            onChange={this.handleChange}
          />

          <HorizontalForm.Checkbox
            label="Add Primary Address"
            name="showAddressFields"
            value={this.state.showAddressFields}
            onChange={this.toggleAddressFields}
          />

          { this.renderAddressFields() }
          {this.props.additionalFields}
        </HorizontalForm>

        <hr />

        <div className="d-flex justify-content-end gap-2">
          <button
            className="btn btn-ghost"
            name="cancel"
            onClick={this.props.onCancel}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!this.isValid()}
            name="save"
            onClick={this.saveCompany}
          >
            {this.state.isSaving ? 'Saving ...' : 'Save Company'}
          </button>
        </div>

      </div>
    );
  }
}
